import { useCallback, useEffect, useRef, useState } from 'react'

import {
  ACTIVITY_WRITE_THROTTLE_MS,
  getIdleState,
  IDLE_ACTIVITY_STORAGE_KEY,
  IDLE_SESSION_CHANNEL,
  IDLE_TICK_MS,
  IdlePhase,
  type IdleSessionMessage,
  IdleSessionMessageType,
  isIdleSessionMessage,
  latestActivity,
  parseStoredActivity,
} from './idle-session.utils'

const ACTIVITY_EVENTS = [
  'mousemove',
  'mousedown',
  'keydown',
  'touchstart',
  'scroll',
  'click',
] as const

function readStoredActivity(): number | null {
  try {
    return parseStoredActivity(window.localStorage.getItem(IDLE_ACTIVITY_STORAGE_KEY))
  } catch {
    return null
  }
}

function writeStoredActivity(at: number): void {
  try {
    window.localStorage.setItem(IDLE_ACTIVITY_STORAGE_KEY, String(at))
  } catch {
    // Storage can be unavailable (private mode, quota); the broadcast still reaches other tabs.
  }
}

function openChannel(): BroadcastChannel | null {
  return typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel(IDLE_SESSION_CHANNEL)
}

type UseIdleSignOutOptions = {
  timeoutMinutes: number | null
  warningSeconds: number
  onExpire: () => void
}

export type IdleSignOutState = {
  isWarning: boolean
  secondsRemaining: number
  staySignedIn: () => void
}

/**
 * One idle clock shared by every tab: activity in any tab (localStorage + BroadcastChannel) keeps
 * the whole session alive, so an idle background tab can no longer sign out a tab someone is
 * working in. The clock is re-read on every tick and whenever the tab becomes visible, so a laptop
 * that slept past the timeout signs out on wake instead of trusting a throttled timer.
 */
export const useIdleSignOut = ({
  timeoutMinutes,
  warningSeconds,
  onExpire,
}: UseIdleSignOutOptions): IdleSignOutState => {
  const [state, setState] = useState<{ phase: IdlePhase; secondsRemaining: number }>({
    phase: IdlePhase.Active,
    secondsRemaining: 0,
  })
  const lastActivityRef = useRef(Date.now())
  const lastWriteRef = useRef(0)
  const channelRef = useRef<BroadcastChannel | null>(null)
  const phaseRef = useRef(IdlePhase.Active)
  const onExpireRef = useRef(onExpire)
  onExpireRef.current = onExpire

  const recordActivity = useCallback((force: boolean) => {
    const now = Date.now()
    lastActivityRef.current = now
    if (!force && now - lastWriteRef.current < ACTIVITY_WRITE_THROTTLE_MS) {
      return
    }
    lastWriteRef.current = now
    writeStoredActivity(now)
    const message: IdleSessionMessage = { type: IdleSessionMessageType.Activity, at: now }
    channelRef.current?.postMessage(message)
  }, [])

  const staySignedIn = useCallback(() => {
    recordActivity(true)
    phaseRef.current = IdlePhase.Active
    setState((current) => ({ ...current, phase: IdlePhase.Active }))
  }, [recordActivity])

  useEffect(() => {
    if (timeoutMinutes) {
      return
    }
    // A tab whose location has no timeout still counts as activity, otherwise a timed-out tab
    // for another location signs the person out while they are working here.
    const channel = openChannel()
    channelRef.current = channel
    const handleActivity = () => recordActivity(false)
    recordActivity(true)
    ACTIVITY_EVENTS.forEach((event) =>
      window.addEventListener(event, handleActivity, { passive: true })
    )
    return () => {
      ACTIVITY_EVENTS.forEach((event) => window.removeEventListener(event, handleActivity))
      channel?.close()
      channelRef.current = null
    }
  }, [timeoutMinutes, recordActivity])

  useEffect(() => {
    if (!timeoutMinutes) {
      return
    }
    const timeoutMs = timeoutMinutes * 60_000
    const warningMs = warningSeconds * 1000
    const channel = openChannel()
    channelRef.current = channel
    let hasExpired = false

    const expire = (announce: boolean) => {
      if (hasExpired) {
        return
      }
      hasExpired = true
      if (announce) {
        const message: IdleSessionMessage = { type: IdleSessionMessageType.Expired }
        channel?.postMessage(message)
      }
      onExpireRef.current()
    }

    const tick = () => {
      const next = getIdleState({
        lastActivityAt: latestActivity(lastActivityRef.current, readStoredActivity()),
        now: Date.now(),
        timeoutMs,
        warningMs,
      })
      if (next.phase === IdlePhase.Expired) {
        expire(true)
        return
      }
      phaseRef.current = next.phase
      setState(next)
    }

    const handleActivity = () => {
      if (phaseRef.current !== IdlePhase.Warning) {
        recordActivity(false)
      }
    }

    const handleMessage = (event: MessageEvent<unknown>) => {
      if (!isIdleSessionMessage(event.data)) {
        return
      }
      if (event.data.type === IdleSessionMessageType.Expired) {
        expire(false)
        return
      }
      lastActivityRef.current = Math.max(lastActivityRef.current, event.data.at)
      tick()
    }

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        tick()
      }
    }

    recordActivity(true)
    const interval = window.setInterval(tick, IDLE_TICK_MS)
    ACTIVITY_EVENTS.forEach((event) =>
      window.addEventListener(event, handleActivity, { passive: true })
    )
    document.addEventListener('visibilitychange', handleVisibility)
    window.addEventListener('focus', handleVisibility)
    channel?.addEventListener('message', handleMessage)

    return () => {
      window.clearInterval(interval)
      ACTIVITY_EVENTS.forEach((event) => window.removeEventListener(event, handleActivity))
      document.removeEventListener('visibilitychange', handleVisibility)
      window.removeEventListener('focus', handleVisibility)
      channel?.removeEventListener('message', handleMessage)
      channel?.close()
      channelRef.current = null
      phaseRef.current = IdlePhase.Active
      setState({ phase: IdlePhase.Active, secondsRemaining: 0 })
    }
  }, [timeoutMinutes, warningSeconds, recordActivity])

  return {
    isWarning: !!timeoutMinutes && state.phase === IdlePhase.Warning,
    secondsRemaining: state.secondsRemaining,
    staySignedIn,
  }
}
